import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BeforeCapture, DragDropContext, DragStart, DragUpdate, DropResult } from '@hello-pangea/dnd';
import { throttle } from 'throttle-debounce';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';

import { selectIds } from '../../../../selectors/ids';
import {
  DraggableHeaderCell,
  DroppableHeaderRow,
  HeaderCell,
  HeaderContent,
  HeaderRow,
  IDroppableAreaPosition,
} from '../../../../components/Header';
import { HeaderFieldContainer, HeaderRowTitleContainer } from '../../../Table/parts/Field';
import { changeOffersOrder } from '../../../../actions/compare';
import { DRAG_SCROLL_THROTTLE_DELAY } from '../../../../constants/delays';
import { selectOffers } from '../../../../selectors/offers';
import { IOffer } from '../../../../types/offer';
import { createDraggablePortalElement } from './helpers';
import { TABLE_COL_WIDTH } from '../../../../constants/table';
import { trackDragDropEvent } from '../../tracking';
import { OfferContext } from '../../../../utils/offerContext';
import { DifferencesSwitchContainer } from '../../../DifferenciesSwitch';
import { TThunkDispatch } from '../../../../types/redux/thunk';
import { trackOfferTeaserShow } from './tracking';

export interface IDraggableHeaderRowContainerProps {
  afterDragEnd?(): void;
  droppableId: string;
  headerRowRef?: React.Ref<HTMLDivElement>;
  onDragDrop?(state: boolean): void;
  onUpdatePosition(position: number): void;
  position: number;
  prev: React.ReactNode;
  sticky?: boolean;
}

export const DraggableHeaderRowContainer = ({
  afterDragEnd,
  droppableId,
  headerRowRef,
  onDragDrop,
  onUpdatePosition,
  position,
  prev,
  sticky,
}: IDraggableHeaderRowContainerProps): React.JSX.Element => {
  const [draggableIndex, setDraggableIndex] = React.useState<number>(-1);
  const [droppableIndex, setDroppableIndex] = React.useState<number>(-1);
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggablePortal, setDraggablePortal] = React.useState<HTMLDivElement | null>(null);
  const [dropAreaPosition, setDropAreaPosition] = React.useState<IDroppableAreaPosition | undefined>(undefined);
  const [isDropEventSent, setDropEventSent] = React.useState(false);
  const droppableAreaRef = React.useRef<HTMLDivElement>(null);
  const listenDropAreaScrollRef = React.useRef<boolean>(false);

  const ids = useSelector(selectIds);
  const offers = useSelector(selectOffers);
  const dispatch = useDispatch<TThunkDispatch>();

  const handleBeforeCapture = React.useCallback(
    (before: BeforeCapture) => {
      const portalEl = createDraggablePortalElement(before.draggableId);
      setDraggablePortal(portalEl);
      setIsDragging(true);

      if (droppableAreaRef.current) {
        const { top, left } = droppableAreaRef.current.getBoundingClientRect();
        const leftPosition = left + TABLE_COL_WIDTH * position;

        setDropAreaPosition({ top, left: leftPosition, right: 0 });
        droppableAreaRef.current.scroll(TABLE_COL_WIDTH * position, 0);
        listenDropAreaScrollRef.current = true;
      }
    },
    [position],
  );

  const handleDragStart = React.useCallback(
    (start: DragStart) => {
      setDraggableIndex(start.source.index);
      setDroppableIndex(start.source.index);

      if (onDragDrop) {
        onDragDrop(true);
      }
    },
    [onDragDrop],
  );

  const handleDragUpdate = React.useCallback(({ source, destination }: DragUpdate) => {
    setDroppableIndex(destination ? destination.index : source.index);
  }, []);

  const handleDragEnd = React.useCallback(
    (result: DropResult) => {
      if (result.destination && result.source.index !== result.destination.index) {
        dispatch(changeOffersOrder({ fromIndex: result.source.index, toIndex: result.destination.index }));

        if (!isDropEventSent) {
          trackDragDropEvent();
          setDropEventSent(true);
        }
      }

      if (draggablePortal) {
        draggablePortal.remove();
        setDraggablePortal(null);
      }

      listenDropAreaScrollRef.current = false;

      if (droppableAreaRef.current) {
        droppableAreaRef.current.scroll(0, 0);
      }

      setDraggableIndex(-1);
      setDroppableIndex(-1);
      setDropAreaPosition(undefined);

      if (onDragDrop) {
        onDragDrop(false);
      }

      setTimeout(() => {
        setIsDragging(false);

        if (afterDragEnd) {
          afterDragEnd();
        }
      }, 0);
    },
    [dispatch, isDropEventSent, draggablePortal, onDragDrop, afterDragEnd],
  );

  // oxlint-disable-next-line react/exhaustive-deps
  const handleScrollDropArea = React.useCallback(
    throttle(DRAG_SCROLL_THROTTLE_DELAY, () => {
      if (droppableAreaRef.current && listenDropAreaScrollRef.current) {
        const left = droppableAreaRef.current.scrollLeft;
        const position = (((left * 100) / TABLE_COL_WIDTH + 0.5) << 0) / 100;

        onUpdatePosition(position);
      }
    }),
    [onUpdatePosition],
  );

  return (
    <DragDropContext
      onBeforeCapture={handleBeforeCapture}
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}
    >
      <HeaderContent sticky={sticky}>
        <HeaderRow ref={headerRowRef} position={position} sticky={sticky}>
          <HeaderCell position={0} sticky={sticky}>
            {sticky ? <DifferencesSwitchContainer /> : <HeaderRowTitleContainer />}
          </HeaderCell>
          <DroppableHeaderRow
            droppableAreaPosition={dropAreaPosition}
            droppableId={droppableId}
            droppableIndex={droppableIndex}
            droppableRef={droppableAreaRef}
            onDropAreaScroll={handleScrollDropArea}
            position={position}
            dragging={isDragging}
          >
            {ids.map((id, i) => {
              const offer: IOffer = offers[id];
              const { published } = offer;

              const position = ids.findIndex(item => item === id);

              return (
                <OfferContext.Provider key={`cell/${id}`} value={offer}>
                  <DraggableHeaderCell
                    published={published}
                    draggableId={`${droppableId}-cell/${id}`}
                    index={i}
                    portalElement={draggableIndex === i ? draggablePortal : null}
                  >
                    <ActionAfterViewObserver
                      percentVisible={0.5}
                      callback={() => trackOfferTeaserShow(id, position)}
                      triggerOnce
                      callbackDelay={1000}
                      trackVisibility
                    >
                      <HeaderFieldContainer dragging={draggableIndex === i} sticky={sticky} />
                    </ActionAfterViewObserver>
                  </DraggableHeaderCell>
                </OfferContext.Provider>
              );
            })}
          </DroppableHeaderRow>
        </HeaderRow>
        {prev}
      </HeaderContent>
    </DragDropContext>
  );
};
